import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import logo from '../assets/logo.png';
import '../styles/Auth.css';
import '../styles/Login.css';

const Spinner = () => <span className="btn-spinner" aria-hidden="true" />;

const Login = () => {
    const [email,    setEmail]    = useState('');
    const [password, setPassword] = useState('');
    const [errors,   setErrors]   = useState({ email: '', password: '', general: '' });
    const [loading,  setLoading]  = useState(false);

    const { login }  = useContext(AuthContext);
    const navigate   = useNavigate();

    const clearField = (field) => {
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const validate = () => {
        const next = { email: '', password: '', general: '' };
        let valid  = true;

        if (!email.trim()) {
            next.email = 'Email is required';
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            next.email = 'Please enter a valid email address';
            valid = false;
        }

        if (!password) {
            next.password = 'Password is required';
            valid = false;
        }

        setErrors(next);
        return valid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setErrors({ email: '', password: '', general: '' });

        try {
            const { success, error } = await login(email, password);
            if (success) {
                navigate('/feed');
            } else {
                setErrors(prev => ({
                    ...prev,
                    general: error || 'Invalid email or password. Please try again.'
                }));
            }
        } catch {
            setErrors(prev => ({
                ...prev,
                general: 'Something went wrong. Please try again.'
            }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">

            {/* ── Left branding panel ── */}
            <div className="auth-left">
                <div className="auth-left-inner">
                    <img src={logo} alt="Comrades" className="auth-logo" />
                    <h1 className="auth-tagline">Connect with your neighbourhood</h1>
                    <ul className="auth-bullets">
                        <li>
                            <span className="auth-bullet-icon">📍</span>
                            Hyper-local posts within your chosen radius
                        </li>
                        <li>
                            <span className="auth-bullet-icon">🤖</span>
                            AI-prioritised feed and real-time safety alerts
                        </li>
                        <li>
                            <span className="auth-bullet-icon">🤝</span>
                            Dual-confirmed transactions and reputation scores
                        </li>
                    </ul>
                </div>
            </div>

            {/* ── Right form panel ── */}
            <div className="auth-right">
                <div className="auth-form-wrap">
                    <h2 className="auth-heading">Welcome back</h2>
                    <p className="auth-sub">Sign in to your Comrades account</p>

                    <form onSubmit={handleSubmit} noValidate>
                        <div className="field">
                            <label htmlFor="login-email">Email</label>
                            <input
                                id="login-email"
                                type="email"
                                value={email}
                                onChange={e => { setEmail(e.target.value); clearField('email'); }}
                                placeholder="you@example.com"
                                disabled={loading}
                                className={errors.email ? 'input--error' : ''}
                                autoComplete="email"
                            />
                            {errors.email && (
                                <span className="field-error" role="alert">{errors.email}</span>
                            )}
                        </div>

                        <div className="field">
                            <label htmlFor="login-password">Password</label>
                            <input
                                id="login-password"
                                type="password"
                                value={password}
                                onChange={e => { setPassword(e.target.value); clearField('password'); }}
                                placeholder="Your password"
                                disabled={loading}
                                className={errors.password ? 'input--error' : ''}
                                autoComplete="current-password"
                            />
                            {errors.password && (
                                <span className="field-error" role="alert">{errors.password}</span>
                            )}
                        </div>

                        {errors.general && (
                            <div className="form-error" role="alert">{errors.general}</div>
                        )}

                        <button type="submit" className="auth-btn" disabled={loading}>
                            {loading ? <><Spinner /> Signing in…</> : 'Sign in'}
                        </button>
                    </form>

                    <p className="auth-switch">
                        No account?{' '}
                        <Link to="/register">Create one here</Link>
                    </p>
                </div>
            </div>

        </div>
    );
};

export default Login;
